/**
 * CDID Tickets tab — the live selection queue: tickets a scheduled CDID run
 * can pick up when admitted, plus trigger-gated and hard-gate-filtered work
 * (project/initiative status, start dates, work containers, blockers).
 * Mirrors a7y-cdid-session.md Phase 1; served by /admin/autobuild/cdid/queue
 * straight from the Linear API.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import type { CdidQueueTicket } from '@a7y/types';
import { api } from '@/lib/api';
import { fmtLocal, fmtUtc, sortByIdDesc, groupByParent, PRIORITY_STYLES } from './shared';

function TicketLink({ ticket }: { ticket: CdidQueueTicket }) {
  return (
    <a
      href={ticket.url}
      target="_blank"
      rel="noreferrer"
      className="font-mono text-xs text-zinc-800 dark:text-zinc-200 hover:underline inline-flex items-center gap-1 shrink-0"
    >
      {ticket.identifier} <ExternalLink className="h-3 w-3 text-zinc-400" />
    </a>
  );
}

export function CdidTicketsTab() {
  const [showExcluded, setShowExcluded] = useState(false);
  const queue = useQuery({
    queryKey: ['cdid-queue'],
    queryFn: () => api.getCdidQueue(),
    refetchInterval: 60_000,
  });

  if (queue.isLoading) {
    return (
      <div className="p-6 text-sm text-zinc-500">Loading the selection queue from Linear…</div>
    );
  }
  if (queue.isError) {
    return (
      <div className="p-4 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 text-sm text-red-700 dark:text-red-300">
        Selection queue failed to load. Check the Worker logs for /admin/autobuild/cdid/queue (a 503
        means LINEAR_API_KEY is not configured on the worker).
      </div>
    );
  }

  const {
    eligible: eligibleRaw = [],
    inFlight: inFlightRaw = [],
    recoveryPending: recoveryPendingRaw = [],
    excluded: excludedRaw = [],
    claimSessions = [],
    claimEvidenceAvailable = false,
    generatedAt,
  } = queue.data ?? {};

  // Newest ticket first, across every list. Display-only — the API still
  // returns `eligible` in the session's own selection order (priority, then
  // oldest first), and that ordering still governs dispatch.
  const eligible = sortByIdDesc(eligibleRaw);
  const inFlight = sortByIdDesc(inFlightRaw);
  const recoveryPending = sortByIdDesc(recoveryPendingRaw);
  const excluded = sortByIdDesc(excludedRaw);
  // Grouped for display only — `eligible` keeps the session's selection order
  // (priority, then oldest first), and groupByParent preserves it within groups.
  const eligibleGroups = groupByParent(eligible);

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-emerald-500 ring-1 ring-emerald-500/20 bg-white dark:border-emerald-500 dark:bg-zinc-900">
        <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-emerald-700 dark:text-emerald-400 border-b border-emerald-100 dark:border-emerald-900">
          Eligible for next scheduled CDID run ({eligible.length})
        </div>
        <div className="px-3 py-2 text-xs text-zinc-600 dark:text-zinc-400 border-b border-zinc-100 dark:border-zinc-800">
          Only tickets that currently pass CDID&apos;s automatic selection gates and are unclaimed
          appear here. They can be picked up for Phase 2 evaluation when a scheduled run is
          admitted.
        </div>
        {eligible.length === 0 ? (
          <div className="p-6 text-sm text-zinc-500">
            {claimSessions.length > 0 && inFlight.length > 0
              ? `No unclaimed tickets currently pass every automatic selection gate — the running CDID session holds ${inFlight.length} active claim${inFlight.length === 1 ? '' : 's'} below.`
              : recoveryPending.length > 0
                ? `${recoveryPending.length} tickets carry stale claims and remain outside eligibility until recovery releases them.`
                : 'No tickets currently pass every automatic selection gate without a CDID claim.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-zinc-50 dark:bg-zinc-900 text-left text-xs text-zinc-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Ticket</th>
                  <th className="px-3 py-2 font-medium">Title</th>
                  <th className="px-3 py-2 font-medium">Priority</th>
                  <th className="px-3 py-2 font-medium">State</th>
                  <th className="px-3 py-2 font-medium">Project</th>
                </tr>
              </thead>
              {eligibleGroups.map((g) => (
                <tbody
                  key={g.parentIdentifier ?? '__no_parent__'}
                  className="divide-y divide-zinc-100 dark:divide-zinc-800 bg-white dark:bg-zinc-950"
                >
                  <tr className="bg-zinc-50 dark:bg-zinc-900">
                    <th
                      colSpan={5}
                      className="px-3 py-1.5 text-left text-[11px] font-medium uppercase tracking-wide text-zinc-500"
                    >
                      {g.label}{' '}
                      <span className="text-zinc-400 font-normal">({g.tickets.length})</span>
                    </th>
                  </tr>
                  {g.tickets.map((t) => (
                    <tr key={t.identifier} className="hover:bg-zinc-50 dark:hover:bg-zinc-900">
                      <td className="px-3 py-2 whitespace-nowrap">
                        <TicketLink ticket={t} />
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-700 dark:text-zinc-300">
                        {t.title}
                        {t.dependsOn.length > 0 && (
                          <span
                            className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300"
                            title={`Sequenced after ${t.dependsOn.join(', ')} in the same session — an epic's internal blocks are implementation order, not deploy gates`}
                          >
                            after {t.dependsOn.join(', ')}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {t.priorityLabel ? (
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${PRIORITY_STYLES[t.priorityLabel] ?? PRIORITY_STYLES.Low}`}
                          >
                            {t.priorityLabel}
                          </span>
                        ) : (
                          <span className="text-xs text-zinc-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap">{t.stateName}</td>
                      <td className="px-3 py-2 text-xs text-zinc-500">{t.projectName ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-zinc-400 border-b border-zinc-100 dark:border-zinc-800">
          {!claimEvidenceAvailable
            ? `Claimed (${inFlight.length}) — run evidence unavailable; active vs. stale cannot be determined`
            : claimSessions.length === 1 && inFlight.length > 0
              ? `In flight (${inFlight.length}) — claimed by CDID session ${claimSessions[0].stamp}${claimSessions[0].runId ? ` (run ${claimSessions[0].runId})` : ''}`
              : inFlight.length > 0
                ? `In flight (${inFlight.length}) — claimed by ${claimSessions.length} live runs`
                : 'In flight (0) — no active CDID claims'}
        </div>
        {inFlight.length === 0 ? (
          <div className="px-3 py-2 text-sm text-zinc-500">
            {claimEvidenceAvailable
              ? 'No tickets have an active CDID claim.'
              : 'No claimed tickets were returned, but active and stale claim states cannot be verified.'}
          </div>
        ) : (
          <div className="divide-y divide-zinc-50 dark:divide-zinc-800">
            {inFlight.map((t) => (
              <div key={t.identifier} className="flex items-center gap-3 px-3 py-1.5">
                <TicketLink ticket={t} />
                <span
                  className="text-xs text-zinc-600 dark:text-zinc-400 truncate grow"
                  title={t.title}
                >
                  {t.title}
                </span>
                <span className="text-[11px] text-zinc-400 shrink-0">{t.stateName} · batched</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-amber-200 dark:border-amber-900 bg-white dark:bg-zinc-900">
        <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-amber-600 dark:text-amber-400 border-b border-amber-100 dark:border-amber-900">
          Recovery pending ({recoveryPending.length}) — carries the cdid claim label, but no CDID
          run is live. Left by a crashed session; the next session&apos;s Part A sweep releases
          these, then returns them to ordinary selection consideration.
        </div>
        {recoveryPending.length === 0 ? (
          <div className="px-3 py-2 text-sm text-zinc-500">No stale CDID claims need recovery.</div>
        ) : (
          <div className="divide-y divide-zinc-50 dark:divide-zinc-800">
            {recoveryPending.map((t) => (
              <div key={t.identifier} className="flex items-center gap-3 px-3 py-1.5">
                <TicketLink ticket={t} />
                <span
                  className="text-xs text-zinc-600 dark:text-zinc-400 truncate grow"
                  title={t.title}
                >
                  {t.title}
                </span>
                <span className="text-[11px] text-amber-600 dark:text-amber-400 shrink-0">
                  {t.stateName} · stale claim
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <button
          type="button"
          onClick={() => setShowExcluded((v) => !v)}
          className="w-full px-3 py-2 text-[11px] uppercase tracking-wide text-zinc-400 flex items-center gap-1 hover:text-zinc-600 dark:hover:text-zinc-300"
        >
          {showExcluded ? (
            <ChevronDown className="h-3 w-3" />
          ) : (
            <ChevronRight className="h-3 w-3" />
          )}
          Gate-filtered ({excluded.length})
        </button>
        {showExcluded && (
          <div className="divide-y divide-zinc-50 dark:divide-zinc-800 border-t border-zinc-100 dark:border-zinc-800">
            {excluded.map((t) => (
              <div key={t.identifier} className="flex items-center gap-3 px-3 py-1.5">
                <TicketLink ticket={t} />
                <span
                  className="text-xs text-zinc-600 dark:text-zinc-400 truncate grow"
                  title={t.title}
                >
                  {t.title}
                </span>
                <span className="text-[11px] text-zinc-400 shrink-0" title={t.reason ?? undefined}>
                  {t.reason}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="text-[11px] text-zinc-400">
        Mirrors the CDID session's Phase-1 selection filter (project/initiative gates, start dates,
        blockers, and explicit deploy labels). Prerequisite work uses blocker relations; calendar
        gates use start dates. An epic's internal
        blocks are implementation sequencing, not deploy gates — a child is eligible once each
        in-epic blocker is merged or is itself eligible, and runs after it in the same session
        ("after A7Y-…"). Claim state comes from run evidence in the flight recorder, not from the
        cdid label alone. Staging and production deploy-trigger tickets belong to the ADGS queue
        before targeted handoff, so they do not appear in this CDID queue. Session history and log
        mentions are separate evidence and do not determine eligibility. Your browser polls this
        page every minute; independently, the API may serve a cached snapshot for up to 60 seconds.
        {generatedAt ? (
          <>
            {' '}
            {/* The viewer's zone, with the UTC instant in the hover so the
                snapshot can still be matched against a UTC-stamped log. */}
            Snapshot <span title={fmtUtc(generatedAt)}>{fmtLocal(generatedAt)}</span>.
          </>
        ) : null}
      </div>
    </div>
  );
}
